import {useEffect,useRef,useState} from 'react';
import {acceptCommunityInvitation} from './api';
import {clearCommunitySession,hasCommunitySession} from './auth';
import {clearPendingInvitationToken,pendingInvitationToken} from './invitations';
import {useCommunitySession} from './useCommunitySession';
import {BottomNav,type MainTab} from './components/BottomNav';
import {CommunityAuthGate} from './components/CommunityAuthGate';
import {LockIcon} from './components/Icons';
import {PublicPlaceLanding} from './components/PublicPlaceLanding';
import {Release6Tabs} from './components/Release6Tabs';
import {RoomHub,type RoomTab} from './components/RoomHub';
import {clearRequestedRoom,communityHomeUrl,readRequestedRoomSlug} from './roomLinks';
import type {Space} from './types';

type OpenRoom={space:Space;tab:RoomTab};
type AuthenticatedProps={onLogout:()=>void};

function AuthenticatedCommunity({onLogout}:AuthenticatedProps){
 const[sessionVersion,setSessionVersion]=useState(0);
 const pilot=useCommunitySession(sessionVersion);
 const[tab,setTab]=useState<MainTab>('rooms');
 const[openRoom,setOpenRoom]=useState<OpenRoom|null>(null);
 const[focusedSlug]=useState(readRequestedRoomSlug);
 const[roomInvitation,setRoomInvitation]=useState(()=>pendingInvitationToken());
 const[invitationNotice,setInvitationNotice]=useState('');
 const[invitationError,setInvitationError]=useState('');
 const invitationAttempted=useRef(false);
 const scrollRef=useRef<HTMLDivElement>(null);
 const focusedSpace=pilot.accessible.find(space=>space.slug===focusedSlug);

 useEffect(()=>{
  if(!roomInvitation||!pilot.participant||pilot.loading||invitationAttempted.current)return;
  invitationAttempted.current=true;
  setInvitationError('');
  void acceptCommunityInvitation(roomInvitation)
   .then(()=>{
    clearPendingInvitationToken();
    setRoomInvitation('');
    setInvitationNotice('Deine persönliche Einladung wurde angenommen. Der neue Raum ist jetzt geöffnet.');
    setSessionVersion(value=>value+1);
   })
   .catch(reason=>{
    invitationAttempted.current=false;
    setInvitationError(reason instanceof Error?reason.message:'Die persönliche Einladung konnte nicht angenommen werden.');
   });
 },[roomInvitation,pilot.participant,pilot.loading]);

 function changeTab(next:MainTab){
  setTab(next);
  requestAnimationFrame(()=>scrollRef.current?.scrollTo({top:0,behavior:'auto'}));
 }

 if(focusedSlug){
  if(pilot.loading)return <div className="loading"><img src="/kr-logo.svg" alt=""/><p>Der Ort wird geöffnet …</p></div>;
  if(pilot.participant&&focusedSpace){
   clearRequestedRoom();
   return <RoomHub space={focusedSpace} participant={pilot.participant} identity={pilot.identity} initialTab={focusedSpace.space_type==='physical_place'?'place':'overview'} onClose={()=>window.location.assign(communityHomeUrl())} focused/>;
  }
  return <PublicPlaceLanding slug={focusedSlug} memberName={pilot.participant?.name||null}/>;
 }

 if(pilot.loading)return <div className="loading"><img src="/kr-logo.svg" alt=""/><p>Deine Räume werden geöffnet …</p></div>;
 if(!pilot.participant)return <CommunityAuthGate initialError={pilot.error} onAuthenticated={()=>setSessionVersion(value=>value+1)}/>;
 if(pilot.error)return <div className="gate"><img src="/kr-logo.svg" alt="Katrin & Rayk"/><h1>Deine Räume sind da.</h1><p>{pilot.error}</p><button className="community-auth-primary" onClick={()=>setSessionVersion(value=>value+1)}>Räume neu laden</button><button className="community-logout" onClick={onLogout}>Abmelden</button></div>;
 if(!pilot.accessible.length&&!roomInvitation)return <div className="gate"><img src="/kr-logo.svg" alt="Katrin & Rayk"/><h1>Noch kein Raum für dich.</h1><p>Dein Profil ist vorhanden, aber aktuell ist noch kein gemeinsamer Raum freigeschaltet.</p><button className="community-auth-primary" onClick={onLogout}>Abmelden</button></div>;

 const open=(space:Space,roomTab:RoomTab)=>setOpenRoom({space,tab:roomTab});
 return <div className="app-shell">
  <div className="app-scroll" ref={scrollRef}>
   <header className="brand-header">
    <div className="brand-row">
     <img className="brand-logo" src="/kr-logo.svg" alt="Katrin & Rayk"/>
     <div className="brand-actions">
      <span className="protected"><LockIcon/> Deine geschützten Räume</span>
      <button className="community-logout" onClick={onLogout}>Abmelden</button>
     </div>
    </div>
    <span className="pilot-label">COMMUNITY · RÄUME · AUSTAUSCH</span>
    <h1>Gemeinsame<br/>Erfahrungsräume</h1>
    <p className="claim"><i/>Erleben. Teilen. Wachsen.</p>
   </header>
   <main>
    {invitationNotice&&<div className="community-invitation-banner success" role="status">{invitationNotice}</div>}
    {invitationError&&<div className="community-invitation-banner error" role="alert"><strong>Die Einladung passt nicht zu diesem Zugang.</strong><span>{invitationError}</span><button type="button" onClick={onLogout}>Mit anderem Zugang anmelden</button></div>}
    <Release6Tabs tab={tab} spaces={pilot.spaces} accessible={pilot.accessible} stats={pilot.stats} identity={pilot.identity} safety={pilot.safety} localVault={pilot.localVault} owner={pilot.owner} onTab={changeTab} onOpen={open} onSpacesChanged={pilot.refreshSpaces}/>
   </main>
  </div>
  <BottomNav active={tab} onChange={changeTab}/>
  {openRoom&&<RoomHub key={`${openRoom.space.id}:${openRoom.tab}`} space={openRoom.space} participant={pilot.participant} identity={pilot.identity} initialTab={openRoom.tab} onClose={()=>setOpenRoom(null)}/>} 
 </div>;
}

export default function AppRelease7(){
 const[authenticated,setAuthenticated]=useState(hasCommunitySession);
 function logout(){clearCommunitySession();setAuthenticated(false);}
 if(!authenticated)return <CommunityAuthGate onAuthenticated={()=>setAuthenticated(true)}/>;
 return <AuthenticatedCommunity onLogout={logout}/>;
}
